import React, { useContext, useMemo } from 'react';
import { ColumnProps } from 'antd/lib/table';
import tinycolor from 'tinycolor2';

import ProfitabilityContext from '@totem/components/ticketing/details/profitability/profitabilityContext';
import { ProfitabilityTaskWrapper } from '@totem/components/ticketing/details/profitability/types';
import { taskStatusToColor } from '@totem/components/ticketing/details/tasks/utilities';
import TicketContext from '@totem/components/ticketing/details/ticketContext';
import IBTable from '@totem/components/ui_components/antDesignOverrides/IBTable';
import Hours from '@totem/components/ui_components/widgets/hours/hours';
import { statisticsStyles } from '@totem/components/ui_components/widgets/statistic/IBStatistic';
import { formatDateOnly, isNotNull } from '@totem/utilities/common';
import {
  sortNumberAscending,
  sortStringAscending,
} from '@totem/utilities/tableUtilities';

const TasksTable = () => {
  const { data, loading } = useContext(ProfitabilityContext);
  const { ticket } = useContext(TicketContext);

  const displayTask = useMemo(() => {
    const taskToShow =
      isNotNull(data) && isNotNull(data.tasks)
        ? data.tasks.filter(
            (item) =>
              item.task.type.toLowerCase() === 'task' &&
              (item.task.actualHours > item.task.budgetedHours ||
                (item.task.status === 'COMPLETED' &&
                  item.task.actualHours < item.task.budgetedHours * 0.5)),
          )
        : [];
    return taskToShow;
  }, [ticket]);

  const getResourceName = (resId: string) => {
    if (isNotNull(ticket)) {
      const resource = ticket.ticket.partiesInvolved.find(
        (item) => item.id === resId,
      );
      if (isNotNull(resource)) {
        return <div>{resource.name}</div>;
      }
    }
    return <span />;
  };

  const columns: ColumnProps<ProfitabilityTaskWrapper>[] = [
    {
      title: 'Task Name',
      dataIndex: 'task.name',
      key: 'name',
      render: (_, record: ProfitabilityTaskWrapper) => (
        <span>{record.task.name}</span>
      ),
      showSorterTooltip: true,
      sortDirections: ['ascend', 'descend'],
      sorter: (compA, compB) =>
        sortStringAscending(compA.task.name, compB.task.name),
    },
    {
      title: 'Status',
      dataIndex: 'task.status',
      key: 'type',
      render: (_, record: ProfitabilityTaskWrapper) => (
        <span>{record.task.status}</span>
      ),
      onCell: (record) => ({
        style: {
          backgroundColor: tinycolor
            .mix(taskStatusToColor(record.task.status), '#FFFFFF', 80)
            .toHexString(),
        },
      }),
      showSorterTooltip: true,
      sortDirections: ['ascend', 'descend'],
      sorter: (compA, compB) =>
        sortStringAscending(compA.task.status, compB.task.status),
    },
    {
      title: 'Assignee',
      dataIndex: 'task.status',
      key: 'type',
      render: (_, record: ProfitabilityTaskWrapper) => {
        if (
          isNotNull(record) &&
          isNotNull(record.task) &&
          isNotNull(record.task.assignedResources)
        ) {
          return record.task.assignedResources.map((res) =>
            getResourceName(res),
          );
        }
        return '';
      },
      showSorterTooltip: true,
      sortDirections: ['ascend', 'descend'],
      sorter: (compA, compB) =>
        sortStringAscending(compA.task.status, compB.task.status),
    },
    {
      title: 'Budgeted Hours',
      dataIndex: 'task.budgetedHours',
      key: 'budgetedHours',
      align: 'right',
      render: (_, record: ProfitabilityTaskWrapper) => (
        <Hours amount={record.task.budgetedHours} />
      ),
      showSorterTooltip: true,
      sortDirections: ['ascend', 'descend'],
      sorter: (compA, compB) =>
        sortNumberAscending(compA.task.budgetedHours, compB.task.budgetedHours),
    },
    {
      title: 'Actual Hours',
      dataIndex: 'task.actualHours',
      key: 'actualHours',
      align: 'right',
      render: (_, record: ProfitabilityTaskWrapper) => (
        <Hours amount={record.task.actualHours} />
      ),
      showSorterTooltip: true,
      sortDirections: ['ascend', 'descend'],
      sorter: (compA, compB) =>
        sortNumberAscending(compA.task.actualHours, compB.task.actualHours),
    },
    {
      title: 'Hours Variance',
      dataIndex: 'task.diff',
      key: 'difference',
      align: 'right',
      render: (_, record: ProfitabilityTaskWrapper) => (
        <Hours amount={record.task.budgetedHours - record.task.actualHours} />
      ),
      onCell: (record) => ({
        style:
          record.task.budgetedHours - record.task.actualHours < 0
            ? statisticsStyles.critical
            : statisticsStyles.ok,
      }),
      showSorterTooltip: true,
      sortDirections: ['ascend', 'descend'],
      sorter: (compA, compB) =>
        sortNumberAscending(
          compA.task.budgetedHours - compA.task.actualHours,
          compB.task.budgetedHours - compB.task.actualHours,
        ),
    },
    {
      title: 'Due Date',
      dataIndex: 'task.dueDate',
      key: 'dueDate',
      align: 'right',
      render: (_, record: ProfitabilityTaskWrapper) => (
        <span>{formatDateOnly(record.task.dueDate)}</span>
      ),
      showSorterTooltip: true,
      sortDirections: ['ascend', 'descend'],
      sorter: (compA, compB) =>
        sortStringAscending(compA.task.dueDate, compB.task.dueDate),
    },
  ];

  return (
    <IBTable
      showSorterTooltip
      columns={columns}
      dataSource={isNotNull(displayTask) ? displayTask : []}
      loading={loading}
      rowKey={(record) => record.task.id}
    />
  );
};

export default TasksTable;
